import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import {
  GradeEntry,
  Assignment,
  EducationLevel,
  QualificationGrade,
} from '../types';

interface GradeCellProps {
  grade?: GradeEntry;
  assignment: Assignment;
  educationLevel: EducationLevel;
  onGradeChange: (grade: Partial<GradeEntry>) => void;
  onShowDetails: () => void;
}

const GradeCell = ({
  grade,
  assignment,
  educationLevel,
  onGradeChange,
  onShowDetails,
}: GradeCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(
    grade?.score?.toString() || grade?.qualification || ''
  );

  const qualificationOptions = [
    { value: 'FB', label: 'FB - Foarte Bine' },
    { value: 'B', label: 'B - Bine' },
    { value: 'S', label: 'S - Suficient' },
    { value: 'I', label: 'I - Insuficient' },
  ];

  const getGradeColor = () => {
    if (!grade) return 'bg-muted';

    if (educationLevel === 'primary') {
      const qual = grade.qualification;
      if (qual === 'FB') return 'bg-success/10 text-success';
      if (qual === 'B') return 'bg-primary/10 text-primary';
      if (qual === 'S') return 'bg-warning/10 text-warning';
      if (qual === 'I') return 'bg-error/10 text-error';
    } else {
      const score = grade.score || 0;
      if (score >= 9) return 'bg-success/10 text-success';
      if (score >= 7) return 'bg-primary/10 text-primary';
      if (score >= 5) return 'bg-warning/10 text-warning';
      return 'bg-error/10 text-error';
    }

    return 'bg-muted';
  };

  const handleSave = () => {
    if (educationLevel === 'primary') {
      onGradeChange({
        qualification: editValue as QualificationGrade,
        lastModified: new Date().toISOString(),
      });
    } else {
      const numericValue = parseFloat(editValue);
      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= assignment.maxScore) {
        onGradeChange({
          score: numericValue,
          lastModified: new Date().toISOString(),
        });
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(grade?.score?.toString() || grade?.qualification || '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="p-2 bg-card border border-border rounded-md">
        {educationLevel === 'primary' ? (
          <Select
            options={qualificationOptions}
            value={editValue}
            onChange={(value) => setEditValue(value.toString())}
            placeholder="Selectează"
          />
        ) : (
          <Input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder="0"
            min={0}
            max={assignment.maxScore}
          />
        )}
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={handleSave}
            className="flex items-center justify-center w-8 h-8 rounded-md bg-success text-white hover:bg-success/90 transition-colors duration-150"
            aria-label="Salvează nota"
          >
            <Icon name="Check" size={16} />
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center justify-center w-8 h-8 rounded-md bg-muted text-foreground hover:bg-muted/80 transition-colors duration-150"
            aria-label="Anulează editarea"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative p-3 rounded-md cursor-pointer transition-all duration-150 hover:shadow-md ${getGradeColor()}`}
      onClick={() => setIsEditing(true)}
      onContextMenu={(e) => {
        e.preventDefault();
        onShowDetails();
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {grade
            ? educationLevel === 'primary'
              ? grade.qualification
              : `${grade.score}/${assignment.maxScore}`
            : '-'}
        </span>
        <Icon
          name="Edit2"
          size={14}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        />
      </div>
      {grade?.submittedDate && (
        <div className="flex items-center gap-1 mt-1 text-xs opacity-70">
          <Icon name="Calendar" size={10} />
          <span>{new Date(grade.submittedDate).toLocaleDateString('ro-RO')}</span>
        </div>
      )}
    </div>
  );
};

export default GradeCell;
